export type Environment = "local" | "development" | "test" | "production";
export interface EnvironmentConfig {
  apiBaseUrl: string;
  coreApiBaseUrl: string;
  /** Kong API Gateway URL — when set, both user-management and ctms-core
   *  route through the gateway. Kong handles JWT validation and injects
   *  x-consumer-id / x-tenant-id headers for upstream services. */
  gatewayBaseUrl: string;
  appName: string;
  /**
   * Additional service URLs to ping periodically for keep-alive and health status.
   * Populated from VITE_KEEP_ALIVE_URLS (comma-separated "Name|https://full/path" pairs).
   * The full ping URL (including path) is used — mirrors the wake-up script exactly.
   * Works in any environment — just set the env var for the target deploy.
   */
  keepAliveUrls: { name: string; url: string }[];
}
/**
 * Default app names per environment.
 * All API URLs are derived at runtime from the VITE_GATEWAY_BASE_URL
 * environment variable.  The static configs below are ONLY used as
 * fallbacks when the env var is not set (i.e. local development).
 */
const LOCAL_GATEWAY = "http://localhost:8000";
export const environmentConfigs: Record<Environment, EnvironmentConfig> = {
  local: {
    apiBaseUrl: `${LOCAL_GATEWAY}/api/v1`,
    coreApiBaseUrl: `${LOCAL_GATEWAY}/api/core/v1`,
    gatewayBaseUrl: LOCAL_GATEWAY,
    appName: "CTMS Local",
    keepAliveUrls: [],
  },
  development: {
    apiBaseUrl: "",
    coreApiBaseUrl: "",
    gatewayBaseUrl: "",
    appName: "CTMS Dev",
    keepAliveUrls: [],
  },
  test: {
    apiBaseUrl: "",
    coreApiBaseUrl: "",
    gatewayBaseUrl: "",
    appName: "CTMS Test",
    keepAliveUrls: [],
  },
  production: {
    apiBaseUrl: "",
    coreApiBaseUrl: "",
    gatewayBaseUrl: "",
    appName: "CTMS",
    keepAliveUrls: [],
  },
};
// Determine current environment
export const getCurrentEnvironment = (): Environment => {
  // Check if running on QA URL and route to test API
  if (typeof window !== "undefined" && window.location.hostname) {
    const hostname = window.location.hostname;
    // Route QA app URL to test API
    if (hostname.includes("aiclintrack-qa")) {
      return "test";
    }
    // Route production app URL to production API
    if (hostname.includes("aiclintrack.com") || hostname === "aiclintrack") {
      return "production";
    }
    // Route staging app URL to staging API
    if (hostname.includes("aiclintrack-stg")) {
      return "development";
    }
    // Route dev frontend (ctms-fg) to development API
    if (hostname.includes("ctms-fg")) {
      return "development";
    }
    // Local development URLs
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.startsWith("192.168")
    ) {
      return "local";
    }
  }
  // Check if we're in a Vite environment
  if (import.meta?.env) {
    const mode = import.meta.env.MODE;
    // Map Vite environments to our custom environments
    switch (mode) {
      case "development":
        return "development";
      case "production":
        return "production";
      case "test":
        return "test";
      default:
        return "local";
    }
  }
  // Fallback for non-Vite environments
  return "local";
};
/**
 * Build the final config.  VITE_GATEWAY_BASE_URL is the single source of
 * truth — apiBaseUrl and coreApiBaseUrl are derived from it automatically.
 *
 * Precedence (highest → lowest):
 *   1. VITE_API_BASE_URL / VITE_CORE_API_BASE_URL  (explicit per-service override)
 *   2. VITE_GATEWAY_BASE_URL + path suffix          (recommended — single env var)
 *   3. Static fallback in environmentConfigs          (local dev only)
 */
const getEnvironmentConfig = (): EnvironmentConfig => {
  const env = getCurrentEnvironment();
  const baseConfig = environmentConfigs[env];
  if (import.meta?.env) {
    // Strip trailing slash so paths join cleanly
    const gateway = (
      import.meta.env.VITE_GATEWAY_BASE_URL ??
      baseConfig.gatewayBaseUrl ??
      ""
    ).replace(/\/$/, "");
    /**
     * Parse VITE_KEEP_ALIVE_URLS — comma-separated "Name|https://url" pairs.
     * Example:
     *   VITE_KEEP_ALIVE_URLS=API Gateway|https://aic-api-dev.onrender.com,Admin Service|https://aic-admin-dev.onrender.com
     */
    const keepAliveUrls: { name: string; url: string }[] = (
      import.meta.env.VITE_KEEP_ALIVE_URLS ?? ""
    )
      .split(",")
      .map((entry: string) => entry.trim())
      .filter(Boolean)
      .map((entry: string) => {
        const pipeIdx = entry.indexOf("|");
        if (pipeIdx === -1) {
          // URL only — derive name from hostname
          const name = new URL(entry).hostname.split(".")[0] ?? entry;
          return { name, url: entry };
        }
        return {
          name: entry.slice(0, pipeIdx).trim(),
          url: entry.slice(pipeIdx + 1).trim(),
        };
      });

    return {
      gatewayBaseUrl: gateway,
      apiBaseUrl:
        import.meta.env.VITE_API_BASE_URL ??
        (gateway ? `${gateway}/api/v1` : baseConfig.apiBaseUrl),
      coreApiBaseUrl:
        import.meta.env.VITE_CORE_API_BASE_URL ??
        (gateway ? `${gateway}/api/core/v1` : baseConfig.coreApiBaseUrl),
      appName: import.meta.env.VITE_APP_NAME ?? baseConfig.appName,
      keepAliveUrls,
    };
  }
  return baseConfig;
};
export const currentConfig = getEnvironmentConfig();
